import React from 'react';

/**
 * Sample campaign data for previewing analytics before any real campaigns
 * have been sent. Strictly a local, clearly-labelled preview: nothing here
 * touches the API, rows carry _demo so actions are disabled, and the mode
 * only ever activates from an explicit "Preview with sample data" tap.
 *
 * Dates are generated relative to now so the "still measuring" state (under
 * 72 hours old) always has an example.
 */

const daysAgo = (n) => new Date(Date.now() - n * 864e5).toISOString();

const demoDetail = (links, recipients, extra = {}) => ({ links, recipients, ...extra });

export const buildSampleEmails = () => [
  {
    id: 'demo-1',
    _demo: true,
    name: 'Spring Glow Sale',
    type: 'email',
    status: 'sent',
    subject: '25% Off Everything, This Weekend Only',
    audience: 'All clients',
    audienceSize: 412,
    sentDate: daysAgo(1.2), // fresh: shows the still-measuring state
    stats: { sent: 412, delivered: 402, opened: 118, clicked: 22, bookings: 9, bounced: 10, unsubscribed: 2 },
    _demoDetail: demoDetail(
      [
        { url: 'beu.app/book/yoursalon', clicks: 16 },
        { url: 'beu.app/book/yoursalon/shop', clicks: 6 },
      ],
      [
        { name: 'Sophie Turner', status: 'booked', services: 'Balayage & blow dry', spend: 180 },
        { name: 'Amelia Hart', status: 'clicked' },
        { name: 'Jess Morgan', status: 'opened' },
        { name: 'Hannah Lee', status: 'booked', services: 'Hydra-glow facial', spend: 95 },
        { name: 'Chloe Adams', status: 'opened' },
        { name: 'Megan Price', status: 'delivered' },
        { name: 'Laura Bennett', status: 'clicked' },
        { name: 'Ruby Walsh', status: 'opened' },
        { name: 'Tilly Andrews', status: 'booked', services: 'Cut & finish', spend: 60 },
        { name: 'Georgia Mills', status: 'opened' },
        { name: 'Abi Carter', status: 'delivered' },
        { name: 'Niamh Riley', status: 'clicked' },
      ],
      {
        revenue: 612,
        timeline: [
          { label: '0-6h', opens: 54, clicks: 11 },
          { label: '6-12h', opens: 26, clicks: 5 },
          { label: '12-24h', opens: 18, clicks: 3 },
          { label: '24-48h', opens: 13, clicks: 2 },
          { label: '48-72h', opens: 7, clicks: 1 },
        ],
      }
    ),
  },
  {
    id: 'demo-2',
    _demo: true,
    name: 'Gift Cards for Mother’s Day',
    type: 'email',
    status: 'sent',
    subject: 'The Gift They Actually Want',
    audience: 'All clients',
    audienceSize: 398,
    sentDate: daysAgo(12),
    stats: { sent: 398, delivered: 391, opened: 132, clicked: 31, bookings: 14, bounced: 7, unsubscribed: 1 },
    _demoDetail: demoDetail(
      [
        { url: 'beu.app/book/yoursalon/gift-cards', clicks: 27 },
        { url: 'beu.app/book/yoursalon', clicks: 4 },
      ],
      [
        { name: 'Grace Holloway', status: 'booked', services: 'Gift voucher £50', spend: 50 },
        { name: 'Eva Sinclair', status: 'booked', services: 'Gift voucher £100', spend: 100 },
        { name: 'Poppy Marsh', status: 'clicked' },
        { name: 'Isla Fenton', status: 'opened' },
        { name: 'Daisy Cole', status: 'booked', services: 'Gift voucher £25', spend: 25 },
        { name: 'Nina Patel', status: 'opened' },
      ],
      {
        revenue: 540,
        timeline: [
          { label: '0-6h', opens: 61, clicks: 14 },
          { label: '6-12h', opens: 31, clicks: 8 },
          { label: '12-24h', opens: 21, clicks: 5 },
          { label: '24-48h', opens: 12, clicks: 3 },
          { label: '48-72h', opens: 7, clicks: 1 },
        ],
      }
    ),
  },
  {
    id: 'demo-3',
    _demo: true,
    name: 'We Miss You · quiet clients',
    type: 'email',
    status: 'sent',
    subject: 'It’s Been a While',
    audience: 'Inactive',
    audienceSize: 86,
    sentDate: daysAgo(26),
    stats: { sent: 86, delivered: 83, opened: 19, clicked: 6, bookings: 4 },
    _demoDetail: demoDetail(
      [{ url: 'beu.app/book/yoursalon', clicks: 6 }],
      [
        { name: 'Bethany Shaw', status: 'booked' },
        { name: 'Lucy Grant', status: 'booked' },
        { name: 'Freya Watts', status: 'clicked' },
        { name: 'Erin Doyle', status: 'opened' },
      ]
    ),
  },
  {
    id: 'demo-4',
    _demo: true,
    name: 'New Stylist: Maya',
    type: 'email',
    status: 'sent',
    subject: 'Someone New Behind the Chair',
    audience: 'All clients',
    audienceSize: 405,
    sentDate: daysAgo(41),
    stats: { sent: 405, delivered: 396, opened: 105, clicked: 17, bookings: 7 },
    _demoDetail: demoDetail(
      [{ url: 'beu.app/book/yoursalon/professional/maya', clicks: 14 }],
      [
        { name: 'Holly Burton', status: 'booked' },
        { name: 'Anna Brooke', status: 'clicked' },
        { name: 'Katie Nolan', status: 'opened' },
      ]
    ),
  },
  {
    id: 'demo-5',
    _demo: true,
    name: 'Birthday Treats · April',
    type: 'email',
    status: 'sent',
    subject: 'Happy Birthday! A Treat Inside',
    audience: 'Birthdays this month',
    audienceSize: 23,
    sentDate: daysAgo(58),
    stats: { sent: 23, delivered: 23, opened: 14, clicked: 7, bookings: 5 },
    _demoDetail: demoDetail(
      [{ url: 'beu.app/book/yoursalon', clicks: 7 }],
      [
        { name: 'Rosie Clarke', status: 'booked' },
        { name: 'Ella Frost', status: 'booked' },
        { name: 'Mia Sutton', status: 'clicked' },
      ]
    ),
  },
  {
    id: 'demo-6',
    _demo: true,
    name: 'VIP First Access · December diary',
    type: 'email',
    status: 'sent',
    subject: 'Before Anyone Else',
    audience: 'VIP clients',
    audienceSize: 48,
    sentDate: daysAgo(74),
    stats: { sent: 48, delivered: 47, opened: 29, clicked: 12, bookings: 11 },
    _demoDetail: demoDetail(
      [{ url: 'beu.app/book/yoursalon', clicks: 12 }],
      [
        { name: 'Charlotte Reed', status: 'booked' },
        { name: 'Olivia Marsh', status: 'booked' },
        { name: 'Emily Hayes', status: 'booked' },
      ]
    ),
  },
];

export const buildSampleSms = () => [
  {
    id: 'demo-sms-1',
    _demo: true,
    name: 'Last-minute Saturday slots',
    type: 'sms',
    status: 'sent',
    message: 'Hi {{FirstName}}, a couple of Saturday slots just opened up at {{SalonName}}. First come, first styled: {{BookingLink}}',
    audience: 'All clients',
    audienceSize: 268,
    sentDate: daysAgo(5),
    stats: { sent: 268, delivered: 261, clicked: 34, bookings: 6 },
    _demoDetail: demoDetail(
      [{ url: 'beu.app/book/yoursalon', clicks: 34 }],
      [
        { name: 'Sophie Turner', status: 'booked' },
        { name: 'Amelia Hart', status: 'clicked' },
        { name: 'Jess Morgan', status: 'delivered' },
        { name: 'Hannah Lee', status: 'clicked' },
        { name: 'Chloe Adams', status: 'delivered' },
        { name: 'Megan Price', status: 'failed' },
        { name: 'Laura Bennett', status: 'delivered' },
        { name: 'Ruby Walsh', status: 'booked' },
      ]
    ),
  },
  {
    id: 'demo-sms-2',
    _demo: true,
    name: 'Bank holiday hours',
    type: 'sms',
    status: 'sent',
    message: 'Hi {{FirstName}}, we’re open 10 to 4 this bank holiday Monday. Fancy a refresh? {{BookingLink}}',
    audience: 'All clients',
    audienceSize: 254,
    sentDate: daysAgo(33),
    stats: { sent: 254, delivered: 249 },
  },
];

export const buildSampleAutomationStats = () => ({
  automations: [
    { type: 'reminder', name: 'Appointment reminders', emailSent: 388, smsSent: 412, opened: 244 },
    { type: 'reviewBooster', name: 'Google review booster', emailSent: 102, smsSent: 0, opened: 58, conversions: 17 },
    { type: 'customUpdate', name: 'First visit follow-up', emailSent: 64, smsSent: 0, opened: 31, clicked: 12 },
    { type: 'bookingUpdate', name: 'Booking updates', emailSent: 296, smsSent: 188, opened: 201 },
  ],
});

/** Slim banner shown while sample data is active */
export const SampleDataBanner = ({ onExit }) => (
  <div className="cmp-demo-banner" role="status">
    <span className="cmp-demo-dot" aria-hidden />
    <span className="cmp-demo-text">
      Viewing sample data so you can explore analytics. Nothing here is real, and nothing is saved.
    </span>
    <button type="button" className="cmp-demo-exit" onClick={onExit}>Exit preview</button>
  </div>
);

/** Inline prompt offering the preview when nothing has been sent yet */
export const SampleDataPrompt = ({ onPreview }) => (
  <div className="cmp-demo-prompt">
    <span>Nothing sent yet. Want to see how your analytics will look?</span>
    <button type="button" className="cmp-demo-go" onClick={onPreview}>Preview with sample data</button>
  </div>
);
